import {
  STORE_CONTEXT_ANSWER_MAX_LENGTH,
  STORE_CONTEXT_TOPIC_LABELS,
  STORE_CONTEXT_TOPIC_ORDER,
  type StoreContextConfig,
  type StoreContextEntry,
  type StoreContextTopic,
} from '@/types/configuration'
import {
  ActionIcon,
  Button,
  Card,
  Divider,
  Group,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { useEffect, useMemo } from 'react'
import { ConfigFormHeader, useConfigViewOnly } from '@/components/ConfigFormHeader'

interface StoreContextFormProps {
  data: StoreContextConfig | null
  onSave: (data: StoreContextConfig) => void
  isSaving: boolean
}

const TOPIC_OPTIONS = STORE_CONTEXT_TOPIC_ORDER.map(topic => ({
  value: topic,
  label: STORE_CONTEXT_TOPIC_LABELS[topic],
}))

const TOPIC_ORDER = new Map<StoreContextTopic, number>(STORE_CONTEXT_TOPIC_ORDER.map((topic, index) => [topic, index]))

function emptyEntry(): StoreContextEntry {
  return {
    id: crypto.randomUUID(),
    topic: 'location_rooftop',
    answer: '',
    enabled: true,
  }
}

function sortEntries(entries: StoreContextEntry[]): StoreContextEntry[] {
  return [...entries].sort((a, b) => (TOPIC_ORDER.get(a.topic) ?? 0) - (TOPIC_ORDER.get(b.topic) ?? 0))
}

function toPersistedConfig(values: StoreContextConfig): StoreContextConfig {
  return {
    entries: values.entries
      .map(entry => ({
        ...entry,
        answer: entry.answer.trim(),
      }))
      .filter(entry => entry.answer.length > 0),
  }
}

export function StoreContextForm({ data, onSave, isSaving }: StoreContextFormProps) {
  const viewOnly = useConfigViewOnly()
  const form = useForm<StoreContextConfig>({
    initialValues: {
      entries: [],
    },
  })

  useEffect(() => {
    form.setValues({
      entries: data?.entries?.length ? sortEntries(data.entries) : [],
    })
    form.resetDirty()
  }, [data])

  const sortedEntries = useMemo(() => sortEntries(form.values.entries), [form.values.entries])

  const handleAddEntry = () => {
    form.setFieldValue('entries', [...form.values.entries, emptyEntry()])
  }

  const handleRemoveEntry = (id: string) => {
    form.setFieldValue(
      'entries',
      form.values.entries.filter(entry => entry.id !== id),
    )
  }

  const handleSubmit = form.onSubmit(values => {
    for (const entry of values.entries) {
      const trimmed = entry.answer.trim()
      if (trimmed.length === 0) {
        notifications.show({
          title: 'Validation error',
          message: 'Each entry needs a non-empty answer before saving.',
          color: 'red',
        })
        return
      }
      if (trimmed.length > STORE_CONTEXT_ANSWER_MAX_LENGTH) {
        notifications.show({
          title: 'Validation error',
          message: `Answers must be ${STORE_CONTEXT_ANSWER_MAX_LENGTH} characters or fewer.`,
          color: 'red',
        })
        return
      }
    }

    onSave(toPersistedConfig(values))
  })

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <ConfigFormHeader onReset={() => form.reset()} isSaving={isSaving} isDirty={form.isDirty()} />
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Add authoritative store facts the AI can use when customers ask about location, identity, contact, or
              visit preparation. Disabled entries stay saved but are not sent to the AI.
            </Text>

            <Group justify="space-between">
              <Text fw={600}>FAQ entries</Text>
              <Button disabled={viewOnly} leftSection={<IconPlus size={16} />} variant="light" onClick={handleAddEntry}>
                Add FAQ entry
              </Button>
            </Group>

            {sortedEntries.length === 0 ? (
              <Text size="sm" c="dimmed">
                No FAQ entries yet. Add entries so the AI can answer store-specific questions from dealer-authored copy.
              </Text>
            ) : (
              sortedEntries.map((entry, index) => {
                const entryIndex = form.values.entries.findIndex(item => item.id === entry.id)
                const showTopicHeader =
                  index === 0 || sortedEntries[index - 1]?.topic !== entry.topic

                return (
                  <Stack key={entry.id} gap="sm">
                    {showTopicHeader && (
                      <>
                        {index > 0 && <Divider />}
                        <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                          {STORE_CONTEXT_TOPIC_LABELS[entry.topic]}
                        </Text>
                      </>
                    )}
                    <Card withBorder padding="md" radius="md">
                      <Stack gap="sm">
                        <Group justify="space-between" align="flex-start">
                          <Select
                            label="Topic"
                            required
                            data={TOPIC_OPTIONS}
                            style={{ flex: 1 }}
                            {...form.getInputProps(`entries.${entryIndex}.topic`)}
                          />
                          <Switch
                            label="Enabled"
                            checked={form.values.entries[entryIndex]?.enabled ?? true}
                            onChange={event =>
                              form.setFieldValue(`entries.${entryIndex}.enabled`, event.currentTarget.checked)
                            }
                            mt={24}
                          />
                          <ActionIcon
                            disabled={viewOnly}
                            color="red"
                            variant="subtle"
                            aria-label="Remove FAQ entry"
                            onClick={() => handleRemoveEntry(entry.id)}
                            mt={24}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Group>
                        <Textarea
                          label="Answer"
                          required
                          autosize
                          minRows={6}
                          maxRows={20}
                          maxLength={STORE_CONTEXT_ANSWER_MAX_LENGTH}
                          description={`${form.values.entries[entryIndex]?.answer?.length ?? 0}/${STORE_CONTEXT_ANSWER_MAX_LENGTH} characters`}
                          {...form.getInputProps(`entries.${entryIndex}.answer`)}
                        />
                      </Stack>
                    </Card>
                  </Stack>
                )
              })
            )}
          </Stack>
        </Card>

      </Stack>
    </form>
  )
}
